import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Plus, Edit2, Trash2 } from "lucide-react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}/api`;

const initialForm = {
  category: "GYM",
  merchant_id: "",
  name: "",
  price: "",
  interval_value: 1,
  interval_unit: "MONTH",
  day_domain: "none",
  pt_active_duration: 0,
  pt_active_duration_unit: "DAY",
  featuresText: "",
  popular: false,
  is_active: true,
  sort_order: 0,
};

const formatRupiah = (amount) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(Number(amount || 0));

const dayDomainDefaultValue = "none";
const dayDomainOptions = [
  { value: "none", label: "None" },
  { value: "Senin", label: "Senin" },
  { value: "Selasa", label: "Selasa" },
  { value: "Rabu", label: "Rabu" },
  { value: "Kamis", label: "Kamis" },
  { value: "Jumat", label: "Jumat" },
  { value: "Sabtu", label: "Sabtu" },
  { value: "Minggu", label: "Minggu" },
];
const planCategoryOptions = [
  { value: "GYM", label: "GYM" },
  { value: "CARDIO", label: "CARDIO" },
  { value: "PT", label: "PT" },
  { value: "ALL_ACCESS", label: "ALL ACCESS" },
];
const preferredCategoryOrder = ["GYM", "CARDIO", "PT", "ALL_ACCESS"];
const normalizeIntervalUnit = (value) => {
  const unit = String(value || "MONTH").toUpperCase();
  return ["DAY", "WEEK", "MONTH"].includes(unit) ? unit : "MONTH";
};
const formatIntervalUnitLabel = (unit, category = "") => {
  const normalizedUnit = normalizeIntervalUnit(unit);
  const normalizedCategory = String(category || "").toUpperCase();
  if (normalizedCategory === "PT" && normalizedUnit === "DAY") return "Sesi";
  if (normalizedUnit === "DAY") return "Hari";
  if (normalizedUnit === "WEEK") return "Minggu";
  return "Bulan";
};

export default function Plans() {
  const [plans, setPlans] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState(initialForm);

  const sortedPlans = useMemo(
    () =>
      [...plans].sort((a, b) => {
        const orderA = Number(a.sort_order || 0);
        const orderB = Number(b.sort_order || 0);
        if (orderA !== orderB) return orderA - orderB;
        return Number(a.price || 0) - Number(b.price || 0);
      }),
    [plans]
  );
  const groupedPlans = useMemo(() => {
    const groupedMap = new Map();
    for (const plan of sortedPlans) {
      const categoryKey = String(plan?.category || "GYM").trim().toUpperCase() || "GYM";
      if (!groupedMap.has(categoryKey)) {
        groupedMap.set(categoryKey, []);
      }
      groupedMap.get(categoryKey).push(plan);
    }

    const getCategoryRank = (categoryKey) => {
      const index = preferredCategoryOrder.indexOf(categoryKey);
      return index === -1 ? preferredCategoryOrder.length : index;
    };

    return Array.from(groupedMap.entries())
      .sort(([categoryA], [categoryB]) => {
        const rankA = getCategoryRank(categoryA);
        const rankB = getCategoryRank(categoryB);
        if (rankA !== rankB) return rankA - rankB;
        return categoryA.localeCompare(categoryB);
      })
      .map(([category, items]) => ({ category, items }));
  }, [sortedPlans]);
  const normalizedFormCategory = String(formData.category || "").toUpperCase();
  const isPtCategory = normalizedFormCategory === "PT";
  const isAllAccessCategory = normalizedFormCategory === "ALL_ACCESS";

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, merchantsRes] = await Promise.all([
        axios.get(`${API}/plans`, { withCredentials: true }),
        axios.get(`${API}/merchants`, { withCredentials: true }),
      ]);
      setPlans(Array.isArray(plansRes.data) ? plansRes.data : []);
      setMerchants(Array.isArray(merchantsRes.data) ? merchantsRes.data : []);
    } catch (error) {
      console.error("Failed to fetch plans/merchants:", error);
      alert(error.response?.data?.detail || "Failed to fetch plans");
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingPlan(null);
    setFormData({ ...initialForm, merchant_id: merchants[0]?.id || "" });
    setIsModalOpen(true);
  };

  const openEdit = (plan) => {
    setEditingPlan(plan);
    setFormData({
      category: plan.category || "GYM",
      merchant_id: plan.merchant_id || "",
      name: plan.name || "",
      price: String(plan.price ?? ""),
      interval_value: Number(plan.interval_value || 1),
      interval_unit: normalizeIntervalUnit(plan.interval_unit),
      day_domain: String(plan.day_domain || dayDomainDefaultValue),
      pt_active_duration: Number(plan.pt_active_duration || 0),
      pt_active_duration_unit: normalizeIntervalUnit(plan.pt_active_duration_unit || "DAY"),
      featuresText: Array.isArray(plan.features) ? plan.features.join("\n") : "",
      popular: !!plan.popular,
      is_active: plan.is_active !== false,
      sort_order: Number(plan.sort_order || 0),
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (planId) => {
    if (!window.confirm("Delete this plan?")) return;
    try {
      await axios.delete(`${API}/plans/${planId}`, { withCredentials: true });
      fetchData();
    } catch (error) {
      console.error("Failed to delete plan:", error);
      alert(error.response?.data?.detail || "Failed to delete plan");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const features = formData.featuresText
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);

    const payload = {
      category: String(formData.category || "GYM").toUpperCase(),
      merchant_id: String(formData.merchant_id || "").trim(),
      name: formData.name.trim(),
      coach_id: null,
      coach_name: null,
      price: Number(formData.price),
      interval_value: Number(formData.interval_value || 1),
      interval_unit: String(formData.category || "").toUpperCase() === "PT" ? "DAY" : normalizeIntervalUnit(formData.interval_unit),
      pt_active_duration: Number(formData.pt_active_duration || 0),
      pt_active_duration_unit: String(formData.category || "").toUpperCase() === "PT"
        ? normalizeIntervalUnit(formData.pt_active_duration_unit)
        : "DAY",
      features,
      popular: !!formData.popular,
      is_active: !!formData.is_active,
      sort_order: Number(formData.sort_order || 0),
    };
    if (!payload.merchant_id) {
      alert("Pilih merchant terlebih dahulu.");
      return;
    }

    try {
      if (editingPlan) {
        await axios.put(`${API}/plans/${editingPlan.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/plans`, payload, { withCredentials: true });
      }
      setIsModalOpen(false);
      setFormData({ ...initialForm, merchant_id: merchants[0]?.id || "" });
      fetchData();
    } catch (error) {
      console.error("Failed to save plan:", error);
      alert(error.response?.data?.detail || "Failed to save plan");
    }
  };

  return (
    <DashboardLayout>
      <Header title="Membership Plans" />
      <div className="p-8" data-testid="plans-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-white">Choose Your Plan</h2>
            <p className="text-sm text-zinc-500 mt-1">Manage plans shown on client landing page</p>
          </div>
          <Button
            onClick={openCreate}
            className="bg-[#D4AF37] text-black font-semibold hover:bg-[#C59A27] rounded-sm"
            data-testid="add-plan-btn"
          >
            <Plus className="w-4 h-4 mr-2" strokeWidth={2} />
            Add Plan
          </Button>
        </div>

        {loading ? (
          <div className="bg-zinc-900/50 border border-white/10 rounded-sm p-8">
            <div className="w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : groupedPlans.length > 0 ? (
          <div className="space-y-5">
            <div className="flex flex-wrap gap-2">
              {groupedPlans.map((section) => (
                <Badge
                  key={`summary-${section.category}`}
                  variant="outline"
                  className="rounded-sm border-white/10 bg-zinc-900/40 text-zinc-300"
                >
                  {section.category} - {section.items.length} plan
                </Badge>
              ))}
            </div>
            {groupedPlans.map((section) => (
              <div key={section.category} className="bg-zinc-900/50 border border-white/10 rounded-sm overflow-hidden">
                <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
                  <h3 className="text-sm font-semibold tracking-[0.12em] text-white uppercase">Kategori {section.category}</h3>
                  <span className="text-xs text-zinc-500">{section.items.length} plan</span>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-xs font-bold uppercase tracking-[0.1em] text-zinc-500 py-4">Plan</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-[0.1em] text-zinc-500 py-4">Merchant</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-[0.1em] text-zinc-500 py-4">Harga</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-[0.1em] text-zinc-500 py-4">Interval</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-[0.1em] text-zinc-500 py-4">Features</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-[0.1em] text-zinc-500 py-4">Order</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-[0.1em] text-zinc-500 py-4">Status</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-[0.1em] text-zinc-500 py-4 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {section.items.map((plan, index) => {
                      const normalizedCategory = String(plan.category || "GYM").toUpperCase();
                      const intervalUnit = formatIntervalUnitLabel(plan.interval_unit, normalizedCategory);
                      return (
                        <TableRow key={plan.id || `${section.category}-${index}`} className="border-white/5 hover:bg-zinc-900/50 transition-colors">
                          <TableCell className="py-4">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-white">{plan.name}</span>
                              {plan.popular && (
                                <Badge variant="outline" className="rounded-sm text-xs bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/30">
                                  Popular
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="py-4 text-sm text-zinc-300">{plan.merchant_name || "-"}</TableCell>
                          <TableCell className="py-4 text-sm text-zinc-300">{formatRupiah(plan.price || 0)}</TableCell>
                          <TableCell className="py-4 text-sm text-zinc-300">
                            {Number(plan.interval_value || 1)} {intervalUnit}
                          </TableCell>
                          <TableCell className="py-4 text-sm text-zinc-400">{Array.isArray(plan.features) ? plan.features.length : 0} items</TableCell>
                          <TableCell className="py-4 text-sm text-zinc-400">{Number(plan.sort_order || 0)}</TableCell>
                          <TableCell className="py-4">
                            <Badge
                              variant="outline"
                              className={`rounded-sm text-xs ${plan.is_active !== false ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" : "bg-zinc-500/10 text-zinc-400 border-zinc-500/30"}`}
                            >
                              {plan.is_active !== false ? "Active" : "Inactive"}
                            </Badge>
                          </TableCell>
                          <TableCell className="py-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => openEdit(plan)}
                                className="p-2 rounded-sm hover:bg-zinc-800 transition-colors text-zinc-400 hover:text-white"
                                data-testid={`edit-plan-${section.category}-${index}`}
                              >
                                <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                              </button>
                              <button
                                onClick={() => handleDelete(plan.id)}
                                className="p-2 rounded-sm hover:bg-red-500/10 transition-colors text-zinc-400 hover:text-red-400"
                                data-testid={`delete-plan-${section.category}-${index}`}
                              >
                                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                              </button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-zinc-900/50 border border-white/10 rounded-sm p-8 text-center text-zinc-500 text-sm">
            No plans found
          </div>
        )}

        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-xl">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold">{editingPlan ? "Edit Plan" : "Add Plan"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Merchant</Label>
                  <select
                    value={formData.merchant_id}
                    onChange={(e) => setFormData({ ...formData, merchant_id: e.target.value })}
                    className="h-10 w-full rounded-sm border border-white/10 bg-zinc-800 px-3 text-sm text-white outline-none focus:border-[#D4AF37]"
                    required
                  >
                    <option value="" disabled>Pilih merchant</option>
                    {merchants.map((merchant) => (
                      <option key={merchant.id} value={merchant.id}>
                        {merchant.name}
                      </option>
                    ))}
                  </select>
                  {merchants.length === 0 && (
                    <p className="text-xs text-amber-400">Tambahkan merchant dulu sebelum membuat plan.</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Kategori</Label>
                  <select
                    value={formData.category}
                    onChange={(e) => {
                      const nextCategory = String(e.target.value || "GYM").toUpperCase();
                      setFormData((prev) => ({
                        ...prev,
                        category: nextCategory,
                        interval_unit: nextCategory === "PT" ? "DAY" : normalizeIntervalUnit(prev.interval_unit),
                        pt_active_duration: nextCategory === "PT" ? prev.pt_active_duration : 0,
                      }));
                    }}
                    className="h-10 w-full rounded-sm border border-white/10 bg-zinc-800 px-3 text-sm text-white outline-none focus:border-[#D4AF37]"
                  >
                    {planCategoryOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Plan Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-zinc-800 border-white/10 text-white rounded-sm"
                    required
                    data-testid="plan-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">
                    {isPtCategory ? "Harga Paket PT (Rp)" : isAllAccessCategory ? "Harga All Access (Rp)" : "Harga Plan (Rp)"}
                  </Label>
                  <Input
                    type="number"
                    min={0}
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="bg-zinc-800 border-white/10 text-white rounded-sm"
                    required
                    data-testid="plan-price-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">
                    {isPtCategory ? "Jumlah Sesi" : "Interval"}
                  </Label>
                  <div className="grid grid-cols-[1fr_1fr] gap-2">
                    <Input
                      type="number"
                      min={1}
                      max={120}
                      value={formData.interval_value}
                      onChange={(e) => setFormData({ ...formData, interval_value: e.target.value })}
                      className="bg-zinc-800 border-white/10 text-white rounded-sm"
                    />
                    <select
                      value={isPtCategory ? "DAY" : formData.interval_unit}
                      onChange={(e) => setFormData({ ...formData, interval_unit: e.target.value })}
                      className="h-10 w-full rounded-sm border border-white/10 bg-zinc-800 px-3 text-sm text-white outline-none focus:border-[#D4AF37]"
                      disabled={isPtCategory}
                    >
                      <option value="DAY">{isPtCategory ? "Sesi" : "Hari"}</option>
                      {!isPtCategory && <option value="WEEK">Minggu</option>}
                      <option value="MONTH">Bulan</option>
                    </select>
                  </div>
                  {!isPtCategory && String(formData.interval_unit || '').toUpperCase() === 'DAY' && (
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Domain (Opsional)</Label>
                      <select
                        value={formData.day_domain || dayDomainDefaultValue}
                        onChange={(e) => setFormData({ ...formData, day_domain: e.target.value })}
                        className="h-10 w-full rounded-sm border border-white/10 bg-zinc-800 px-3 text-sm text-white outline-none focus:border-[#D4AF37]"
                      >
                        {dayDomainOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>

              {isPtCategory && (
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">
                    Masa Aktif (Opsional)
                  </Label>
                  <div className="grid grid-cols-[1fr_1fr] gap-2">
                    <Input
                      type="number"
                      min={0}
                      placeholder="0 = Tanpa batas"
                      value={formData.pt_active_duration}
                      onChange={(e) => setFormData({ ...formData, pt_active_duration: Number(e.target.value) })}
                      className="bg-zinc-800 border-white/10 text-white rounded-sm"
                    />
                    <select
                      value={formData.pt_active_duration_unit}
                      onChange={(e) => setFormData({ ...formData, pt_active_duration_unit: e.target.value })}
                      className="h-10 w-full rounded-sm border border-white/10 bg-zinc-800 px-3 text-sm text-white outline-none focus:border-[#D4AF37]"
                    >
                      <option value="DAY">Hari</option>
                      <option value="WEEK">Minggu</option>
                      <option value="MONTH">Bulan</option>
                    </select>
                  </div>
                  <p className="text-xs text-zinc-500">
                    {formData.pt_active_duration > 0
                      ? `Paket berlaku ${formData.pt_active_duration} ${formatIntervalUnitLabel(formData.pt_active_duration_unit)} dari tanggal pembelian.`
                      : "Isi 0 jika tidak ada batas waktu."}
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Features (one line each)</Label>
                <Textarea
                  rows={6}
                  value={formData.featuresText}
                  onChange={(e) => setFormData({ ...formData, featuresText: e.target.value })}
                  className="bg-zinc-800 border-white/10 text-white rounded-sm"
                  data-testid="plan-features-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Sort Order</Label>
                  <Input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })}
                    className="bg-zinc-800 border-white/10 text-white rounded-sm"
                    data-testid="plan-sort-order-input"
                  />
                </div>
                <div className="flex items-center justify-between md:justify-start md:gap-3 pt-5">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Popular</Label>
                  <Switch
                    checked={formData.popular}
                    onCheckedChange={(checked) => setFormData({ ...formData, popular: checked })}
                    data-testid="plan-popular-switch"
                  />
                </div>
                <div className="flex items-center justify-between md:justify-start md:gap-3 pt-5">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Active</Label>
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                    data-testid="plan-active-switch"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="flex-1 border-white/10 text-white hover:bg-zinc-800 rounded-sm">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-[#D4AF37] text-black font-semibold hover:bg-[#C59A27] rounded-sm" data-testid="plan-submit-btn">
                  {editingPlan ? "Update Plan" : "Add Plan"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

